import type { TFunction } from 'i18next';

import { roundForDisplay } from 'app/utils/number';

const getSelectedPlayersStatsLines = <T extends StatPlayer | PlayerBase>(
  playerTeam1: T | undefined,
  playerTeam2: T | undefined,
  videogame: VideogameSlug,
  t: TFunction
): {
  statsLinesWithChart: StatLine<T>[];
  statsLinesWithoutChart: StatLine<T>[];
} => {
  let propertiesWithChart: StatProperties<T>[] = [];
  let propertiesWithoutChart: StatProperties<T>[] = [];

  switch (videogame) {
    case 'dota-2': {
      const dota2PropsWithChart: StatProperties<StatPlayerDota2>[] = [
        {
          name: 'position',
          displayFunction: value => t(`vg-dota2-position-${value}`)
        },
        {
          name: 'average_kda',
          highlighted: 'max',
          displayFunction: roundForDisplay(1)
        },
        {
          name: 'average_gold_per_minute',
          highlighted: 'max',
          displayFunction: roundForDisplay(0)
        },
        {
          name: 'average_xp_per_minute',
          highlighted: 'max',
          displayFunction: roundForDisplay(0)
        },
        {
          name: 'average_creep_score_per_minute',
          highlighted: 'max',
          displayFunction: roundForDisplay(0)
        },
        {
          name: 'average_denies_per_minute',
          highlighted: 'max',
          displayFunction: roundForDisplay(1)
        }
      ];
      propertiesWithChart = dota2PropsWithChart as StatProperties<T>[];

      const dota2PropsWithoutChart: StatProperties<StatPlayerDota2>[] = [
        {
          name: 'average_kill_participation',
          highlighted: 'max',
          displayFunction: roundForDisplay(0, '%')
        },
        {
          name: 'average_hero_damage_per_minute',
          highlighted: 'max',
          displayFunction: roundForDisplay(0)
        },
        {
          name: 'average_wards_placed',
          highlighted: 'max',
          displayFunction: roundForDisplay(1)
        },
        {
          name: 'average_camps_stacked_per_minute',
          highlighted: 'max',
          displayFunction: roundForDisplay(1)
        },
        {
          name: 'average_hero_healing_per_minute',
          highlighted: 'max',
          displayFunction: roundForDisplay(0)
        },
        {
          name: 'average_crowd_control_per_minute',
          highlighted: 'max',
          displayFunction: roundForDisplay(1)
        },
        {
          name: 'average_tower_damage_per_minute',
          highlighted: 'max',
          displayFunction: roundForDisplay(0)
        }
      ];
      propertiesWithoutChart = dota2PropsWithoutChart as StatProperties<T>[];
      break;
    }
    case 'cs-go': {
      const csgoPropsWithChart: StatProperties<StatPlayerCs>[] = [
        {
          name: 'kd_ratio',
          highlighted: 'max',
          displayFunction: roundForDisplay(2)
        },
        {
          name: 'average_damage_per_round',
          highlighted: 'max',
          displayFunction: roundForDisplay(2)
        },
        {
          name: 'average_headshots_per_game',
          highlighted: 'max',
          displayFunction: roundForDisplay(2)
        },
        {
          name: 'average_first_kills_per_game',
          highlighted: 'max',
          displayFunction: roundForDisplay(2)
        },
        {
          name: 'average_multi_kills_per_game',
          highlighted: 'max',
          displayFunction: roundForDisplay(2)
        }
      ];
      propertiesWithChart = csgoPropsWithChart as StatProperties<T>[];
      const csgoPropsWithoutChart: StatProperties<StatPlayerCs>[] = [
        {
          name: 'kills_per_round',
          highlighted: 'max',
          displayFunction: roundForDisplay(2)
        },
        {
          name: 'average_assists_per_round',
          highlighted: 'max',
          displayFunction: roundForDisplay(2)
        },
        {
          name: 'average_flash_assists_per_game',
          highlighted: 'max',
          displayFunction: roundForDisplay(2)
        },
        {
          name: 'deaths_per_round',
          highlighted: 'min',
          displayFunction: roundForDisplay(2)
        },
        {
          name: 'average_utility_kills_per_game',
          highlighted: 'max',
          displayFunction: roundForDisplay(2)
        }
      ];
      propertiesWithoutChart = csgoPropsWithoutChart as StatProperties<T>[];
      break;
    }
  }

  return {
    statsLinesWithChart: propertiesWithChart.map(property => {
      const propertyWithValues: StatLine<T> = {
        ...property,
        team1Value: playerTeam1?.[property.name],
        team2Value: playerTeam2?.[property.name]
      };
      return propertyWithValues;
    }),
    statsLinesWithoutChart: propertiesWithoutChart.map(property => {
      const propertyWithValues: StatLine<T> = {
        ...property,
        team1Value: playerTeam1?.[property.name],
        team2Value: playerTeam2?.[property.name]
      };
      return propertyWithValues;
    })
  };
};

export { getSelectedPlayersStatsLines };
